/** Put holiday pages first when a search clearly asks for a holiday.
 *
 *  "holidays over easter" matches ~240 pages on the word "holidays" alone, so
 *  advice articles that mention Easter outrank the Easter holidays themselves.
 *  When the search reads as a holiday request we pull holiday pages to the top
 *  of the main list. Articles stay, just below them.
 *
 *  A place on its own ("verbier", "austria") is NOT a holiday request: those
 *  searches usually want the resort or country guide, so they keep the normal
 *  order even though the parser turns them into a place filter. */

import type { Chip } from "./departures.js";
import type { SearchDoc } from "./schema.js";

const HOLIDAY_WORDS =
  /\b(holidays?|trips?|breaks?|weeks?|weekends?|getaways?|departures?|tours?|half[- ]?term)\b/i;

/** Parsed filters that only name a place; every other filter (dates, price,
 *  nights, trip type, family…) means the person is shopping for a holiday. */
const PLACE_FIELDS = new Set(["countries", "resorts"]);

export function hasHolidayIntent(q: string, applied: Chip[] = []): boolean {
  if (HOLIDAY_WORDS.test(q)) return true;
  return applied.some((c) => !PLACE_FIELDS.has(c.field));
}

/** Holiday pages first (the dedicated holiday search's order, then any other
 *  holiday hits from the main list), then everything else in its original
 *  order. Each page appears once; the list keeps its original length. */
export function boostHolidays(main: SearchDoc[], holidays: SearchDoc[]): SearchDoc[] {
  const seen = new Set<string>();
  const out: SearchDoc[] = [];
  const add = (h: SearchDoc) => {
    if (seen.has(h.id)) return;
    seen.add(h.id);
    out.push(h);
  };
  holidays.forEach(add);
  main.filter((h) => h.post_type === "holiday").forEach(add);
  main.forEach(add);
  return out.slice(0, main.length);
}
